import { inject, Injectable } from '@angular/core'
import {
  confirmResetPassword,
  type ConfirmResetPasswordInput,
  resendSignUpCode,
  ConfirmSignUpInput,
  autoSignIn,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  signOut,
  signUp,
  AuthUser,
} from 'aws-amplify/auth'
import { signIn, type SignInInput } from 'aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { ToastService } from './toast.service'
import { StoreSession } from 'src/app/store/store-session'
import { AuthService } from '@session/services/auth.service'
import { AppFlowService } from './app-flow.service'
import { GeolocationService } from './geolocation.service'

type PlaywrightCognitoMockMap = Record<string, { type: 'resolve' | 'reject'; value: unknown }>

const runPlaywrightCognitoMock = <T>(method: string): { hasMock: false } | { hasMock: true; value: T } => {
  if (typeof window === 'undefined') {
    return { hasMock: false }
  }

  const mockMap = (window as { __PW_COGNITO_MOCKS__?: PlaywrightCognitoMockMap }).__PW_COGNITO_MOCKS__
  if (!mockMap || !mockMap[method]) {
    return { hasMock: false }
  }

  const mock = mockMap[method]
  if (mock.type === 'reject') {
    throw mock.value
  }

  return { hasMock: true, value: mock.value as T }
}

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  toastService = inject(ToastService)
  readonly store = inject(StoreSession)
  authService = inject(AuthService)
  appFlowService = inject(AppFlowService)
  geolocationService = inject(GeolocationService)

  async handleSignUp(username: string, password: string, firstName: string, lastName: string, phoneNumber: string) {
    const mocked = runPlaywrightCognitoMock<Awaited<ReturnType<typeof signUp>>>('handleSignUp')
    if (mocked.hasMock) return mocked.value

    const resp = await signUp({
      username,
      password,
      options: {
        userAttributes: {
          'custom:firstName': firstName,
          'custom:lastName': lastName,
          'custom:phoneNumber': phoneNumber,
        },
        autoSignIn: true,
      },
    })
    return resp
  }

  async handleSignUpConfirmation({ username, confirmationCode }: ConfirmSignUpInput) {
    const mocked = runPlaywrightCognitoMock<Awaited<ReturnType<typeof confirmSignUp>>>('handleSignUpConfirmation')
    if (mocked.hasMock) return mocked.value

    const resp = await confirmSignUp({
      username,
      confirmationCode,
    })
    return resp
  }

  async handleAutoSignIn() {
    const mocked = runPlaywrightCognitoMock<Awaited<ReturnType<typeof autoSignIn>>>('handleAutoSignIn')
    if (mocked.hasMock) return mocked.value

    const signInOutput = await autoSignIn()
    return signInOutput
  }

  async handleSignIn({ username, password }: SignInInput) {
    const { nextStep } = await signIn({ username, password })
    return nextStep
  }

  async handleSignOut() {
    const mocked = runPlaywrightCognitoMock<void>('handleSignOut')
    if (mocked.hasMock) return mocked.value

    await signOut()
  }

  async handleResetPassword(username: string) {
    const mocked = runPlaywrightCognitoMock<Awaited<ReturnType<typeof resetPassword>>>('handleResetPassword')
    if (mocked.hasMock) return mocked.value

    const output = await resetPassword({ username })
    return output
  }

  async handleConfirmResetPassword({ username, confirmationCode, newPassword }: ConfirmResetPasswordInput) {
    const mocked = runPlaywrightCognitoMock<void>('handleConfirmResetPassword')
    if (mocked.hasMock) return mocked.value

    await confirmResetPassword({ username, confirmationCode, newPassword })
  }

  async handleResendSignUpCode(username: string) {
    const mocked = runPlaywrightCognitoMock<Awaited<ReturnType<typeof resendSignUpCode>>>('handleResendSignUpCode')
    if (mocked.hasMock) return mocked.value

    const resp = await resendSignUpCode({ username })
    return resp
  }

  async currentSession(): Promise<string | undefined> {
    const mocked = runPlaywrightCognitoMock<string | undefined>('currentSession')
    if (mocked.hasMock) return mocked.value

    const { tokens } = await fetchAuthSession({ forceRefresh: true })
    return tokens?.accessToken.toString()
  }

  async currentAuthenticatedUser(): Promise<AuthUser> {
    const mocked = runPlaywrightCognitoMock<AuthUser>('currentAuthenticatedUser')
    if (mocked.hasMock) return mocked.value

    const currentUser: AuthUser = await getCurrentUser()
    return currentUser
  }

  async returnAccesToken(): Promise<string> {
    const mocked = runPlaywrightCognitoMock<string>('returnAccesToken')
    if (mocked.hasMock) return mocked.value

    const token = await cognitoUserPoolsTokenProvider.authTokenStore.loadTokens()
    return token?.accessToken.toString() || ''
  }
}
